import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, ArrowUp } from "lucide-react";

const scrollToY = (y, immediate) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(y, immediate ? { immediate: true } : { duration: 1.8 });
  } else {
    window.scrollTo({ top: y, behavior: immediate ? "auto" : "smooth" });
  }
};

/* La "levetta": trascinala e la storia scorre senza toccare lo scroll.
   Con il tasto play la pagina scorre da sola, come un film. */
export const StoryControls = () => {
  const [progress, setProgress] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [showTop, setShowTop] = useState(false);
  const trackRef = useRef(null);
  const rafRef = useRef(null);
  const draggingRef = useRef(false);

  useEffect(() => {
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const p = max > 0 ? window.scrollY / max : 0;
      setProgress(Math.min(1, Math.max(0, p)));
      setShowTop(window.scrollY > window.innerHeight * 0.8);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!playing) return;
    /* la canzone parte insieme allo scorrimento, ma ognuno va al suo
       passo: la musica si mette in pausa dal tasto in alto */
    const audio = document.getElementById("bg-music");
    if (audio && audio.paused) {
      audio.muted = false;
      audio.currentTime = 0;
      audio.play().catch(() => {});
    }
    /* se siamo già in fondo, il film riparte dall'inizio */
    const maxNow = document.documentElement.scrollHeight - window.innerHeight;
    if (window.scrollY >= maxNow - 10) scrollToY(0, true);
    /* viaggio di durata fissa (~2 minuti), non legato alla canzone */
    const FILM_MS = 120000;
    const remaining =
      document.documentElement.scrollHeight - window.innerHeight - window.scrollY;
    const pxPerMs = Math.max(remaining / FILM_MS, 0.02);
    let last = performance.now();
    const tick = (now) => {
      const dt = Math.min(now - last, 100);
      last = now;
      const y = window.scrollY + pxPerMs * dt;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (y >= max) {
        setPlaying(false);
        return;
      }
      scrollToY(y, true);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    const stop = () => setPlaying(false);
    window.addEventListener("wheel", stop);
    window.addEventListener("touchstart", stop);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("wheel", stop);
      window.removeEventListener("touchstart", stop);
    };
  }, [playing]);

  const dragTo = useCallback((clientY) => {
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const p = Math.min(1, Math.max(0, (clientY - rect.top) / rect.height));
    const max = document.documentElement.scrollHeight - window.innerHeight;
    scrollToY(p * max, true);
  }, []);

  const onPointerDown = (e) => {
    draggingRef.current = true;
    setPlaying(false);
    dragTo(e.clientY);
    const move = (ev) => {
      if (draggingRef.current) dragTo(ev.clientY);
    };
    const up = () => {
      draggingRef.current = false;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  return (
    <>
      <div className="levetta" data-testid="levetta">
        <motion.button
          className={`film-btn ${playing ? "film-btn--on" : ""}`}
          onClick={() => setPlaying((p) => !p)}
          aria-label={playing ? "Metti in pausa la storia" : "La storia scorre da sola"}
          aria-pressed={playing}
          data-testid="film-play-btn"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {playing ? <Pause size={15} /> : <Play size={15} fill="currentColor" />}
        </motion.button>
        <div
          className="levetta-track"
          ref={trackRef}
          onPointerDown={onPointerDown}
          role="slider"
          aria-label="Scorri la storia"
          aria-valuenow={Math.round(progress * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
          data-testid="levetta-track"
        >
          <span className="levetta-fill" style={{ height: `${progress * 100}%` }} />
          <span
            className="levetta-knob"
            style={{ top: `${progress * 100}%` }}
            data-testid="levetta-knob"
          />
        </div>
        <span className="levetta-label">la levetta</span>
      </div>

      <AnimatePresence>
        {showTop && (
          <motion.button
            className="back-top"
            onClick={() => {
              setPlaying(false);
              scrollToY(0, false);
            }}
            aria-label="Torna all'inizio della pagina"
            data-testid="back-to-top-btn"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.35 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <ArrowUp size={17} />
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
};
